import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { queryClient } from '../lib/queryClient';
import type { User } from '../types';
import i18n from '../lib/i18n';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  emailLogin: (email: string, password?: string) => Promise<{ onboardingCompleted: boolean }>;
  register: (email: string, password: string, name?: string, surname?: string) => Promise<void>;
  updateProfile: (data: { name?: string; surname?: string; preferredLanguage?: string }) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  isTrainer: boolean;
  isTrainerOrAdmin: boolean;
  isAdmin: boolean;
  isPowerAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Store only essential fields in localStorage to minimize PII exposure (GDPR). */
function minimalUserForStorage(u: any) {
  return {
    id: u.id,
    role: u.role,
    name: u.name,
    surname: u.surname,
    email: u.email,
    photoUrl: u.photoUrl,
    onboardingCompleted: u.onboardingCompleted,
    gdprConsent: u.gdprConsent,
  };
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.getMe()
        .then((u) => {
          setUser(u);
          // Sync localStorage so NajSportGuard and NajSportEvents see the same user
          localStorage.setItem('najsportUser', JSON.stringify(minimalUserForStorage(u)));
          // Sync preferred language from DB to i18n
          if (u.preferredLanguage) {
            localStorage.setItem('tri2fly-lang', u.preferredLanguage);
            if (i18n.language !== u.preferredLanguage) {
              i18n.changeLanguage(u.preferredLanguage);
            }
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('najsportUser');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    queryClient.clear();
    const { user } = await authAPI.login({ email, password });
    setUser(user);
    localStorage.setItem('najsportUser', JSON.stringify(minimalUserForStorage(user)));
  };

  const emailLogin = async (email: string, password?: string) => {
    queryClient.clear();
    const { token, user: u } = await authAPI.emailLogin(email, password);
    localStorage.setItem('token', token);
    // Fetch full user profile via /me to get all fields
    const fullUser = await authAPI.getMe();
    setUser(fullUser);
    // Sync localStorage so NajSportGuard and NajSportEvents see the same user
    localStorage.setItem('najsportUser', JSON.stringify(minimalUserForStorage(fullUser)));
    return { onboardingCompleted: u.onboardingCompleted };
  };

  const register = async (email: string, password: string, name?: string, surname?: string) => {
    const { user } = await authAPI.register({ email, password, name, surname });
    setUser(user);
  };

  const updateProfile = async (data: { name?: string; surname?: string; preferredLanguage?: string }) => {
    const updatedUser = await authAPI.updateMe(data);
    setUser(updatedUser);
    return updatedUser;
  };

  const logout = () => {
    authAPI.logout();
    localStorage.removeItem('najsportUser');
    setUser(null);
    queryClient.clear(); // Remove all cached data from previous session
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        emailLogin,
        register,
        updateProfile,
        logout,
        isAuthenticated: !!user,
        isTrainer: user?.role === 'TRAINER',
        isTrainerOrAdmin: user?.role === 'TRAINER' || user?.role === 'ADMIN' || user?.role === 'POWER_ADMIN',
        isAdmin: user?.role === 'ADMIN' || user?.role === 'POWER_ADMIN',
        isPowerAdmin: user?.role === 'POWER_ADMIN',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

